import { Modal, Stack, Text, Box, UnstyledButton, Group, Collapse, Checkbox } from '@mantine/core';
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { TreeNode } from '../utils/translationTree';

interface TreeFilterModalProps {
    opened: boolean;
    onClose: () => void;
    tree: TreeNode[];
    selectedPath: string | null;
    onSelect: (path: string | null) => void;
}

interface TreeNodeItemProps {
    node: TreeNode;
    selectedPath: string | null;
    onSelect: (path: string | null) => void;
    level: number;
}

function TreeNodeItem({ node, selectedPath, onSelect, level }: TreeNodeItemProps) {
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedPath === node.path;

    // Check if this node is in the path to the selected node
    const isInSelectedPath = selectedPath?.startsWith(node.path + '.') || false;

    // Auto-expand if this node is in the path to the selected node
    const [expanded, setExpanded] = useState(isInSelectedPath);

    // Update expansion state when selectedPath changes
    useEffect(() => {
        if (isInSelectedPath) {
            setExpanded(true);
        }
    }, [isInSelectedPath]);

    const handleToggle = () => {
        if (hasChildren) {
            setExpanded(!expanded);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (isSelected) {
            onSelect(null);
        } else {
            onSelect(node.path);
        }
    };

    // For leaf nodes, the whole line is clickable
    const handleLineClick = () => {
        if (!hasChildren) {
            if (isSelected) {
                onSelect(null);
            } else {
                onSelect(node.path);
            }
        } else {
            handleToggle();
        }
    };

    return (
        <Box>
            <UnstyledButton
                onClick={handleLineClick}
                style={{
                    width: '100%',
                    padding: '6px 8px',
                    paddingLeft: `${8 + level * 20}px`,
                    cursor: 'pointer',
                }}
                styles={{
                    root: {
                        '&:hover': {
                            backgroundColor: '#f8f9fa',
                        },
                    },
                }}
            >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                        <Checkbox
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            size="xs"
                            styles={{
                                input: {
                                    cursor: 'pointer',
                                },
                            }}
                        />
                        <Text size="sm" style={{ flex: 1 }}>
                            {node.label}
                        </Text>
                    </Group>
                    {hasChildren && (
                        expanded ? (
                            <IconChevronDown size={16} style={{ flexShrink: 0, color: '#868e96' }} />
                        ) : (
                            <IconChevronRight size={16} style={{ flexShrink: 0, color: '#868e96' }} />
                        )
                    )}
                </Group>
            </UnstyledButton>

            {hasChildren && (
                <Collapse in={expanded}>
                    <Box>
                        {node.children!.map((child) => (
                            <TreeNodeItem
                                key={child.path}
                                node={child}
                                selectedPath={selectedPath}
                                onSelect={onSelect}
                                level={level + 1}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
}

export function TreeFilterModal({ opened, onClose, tree, selectedPath, onSelect }: TreeFilterModalProps) {
    const handleSelect = (path: string | null) => {
        onSelect(path);
        // Don't close modal so user can explore the tree
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Filter by Translation Key" size="lg">
            <Stack gap="md">
                {selectedPath && (
                    <Group gap="xs" justify="space-between">
                        <Text size="sm" c="dimmed">
                            Active Filter: <Text component="span" fw={500} c="dark">{selectedPath}</Text>
                        </Text>
                        <UnstyledButton
                            onClick={() => onSelect(null)}
                            style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                color: '#868e96',
                            }}
                            styles={{
                                root: {
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                            }}
                        >
                            Clear
                        </UnstyledButton>
                    </Group>
                )}

                <Box
                    style={{
                        maxHeight: '500px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '4px',
                    }}
                >
                    {tree.length === 0 ? (
                        <Text size="sm" c="dimmed" ta="center" py="xl">
                            No translation keys found
                        </Text>
                    ) : (
                        tree.map((node) => (
                            <TreeNodeItem
                                key={node.path}
                                node={node}
                                selectedPath={selectedPath}
                                onSelect={handleSelect}
                                level={0}
                            />
                        ))
                    )}
                </Box>
            </Stack>
        </Modal>
    );
}
